import { useEffect, lazy, Suspense } from 'react';
import { useApp } from './store';
import Toolbar from './components/Toolbar';
import Sidebar from './components/Sidebar';

// 3D and Flat viewports each pull in different heavy libs (three/r3f for 3D)
// or none (Flat is just SVG). Lazy-load so the initial chunk is small and
// switching only loads what's needed.
const Viewport3D = lazy(() => import('./components/Viewport3D'));
const FlatPreview = lazy(() => import('./components/FlatPreview'));

function ViewportFallback({ label }: { label: string }) {
  return (
    <div className="w-full h-full bg-bg flex items-center justify-center text-mute text-sm">
      Loading {label}…
    </div>
  );
}

export default function App() {
  const showFlat = useApp((s) => s.showFlat);
  const theme = useApp((s) => s.theme);

  // Apply the theme as a class on <html> so the CSS variables in index.css flip.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Don't intercept while typing in inputs
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        useApp.getState().undo();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        useApp.getState().redo();
      } else if (key === 's') {
        e.preventDefault();
        useApp.getState().saveJson();
      } else if (key === 'o') {
        e.preventDefault();
        useApp.getState().loadJson().catch((err) => {
          const msg = err instanceof Error ? err.message : 'Failed to load';
          alert(`Load failed: ${msg}`);
        });
      }
    };

    const onKeyNoMod = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const { selectedId, part, remove } = useApp.getState();
        if (selectedId && selectedId !== part.rootId) {
          e.preventDefault();
          remove(selectedId);
        }
      } else if (e.key === 'Escape') {
        useApp.getState().select(null);
      } else if (e.key === 'f' || e.key === 'F') {
        useApp.getState().bumpFit();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keydown', onKeyNoMod);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keydown', onKeyNoMod);
    };
  }, []);

  return (
    <div className="h-full flex flex-col">
      <Toolbar />
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <main className="flex-1 min-h-0 min-w-0 relative">
          <Suspense fallback={<ViewportFallback label={showFlat ? 'flat preview' : '3D viewport'} />}>
            {showFlat ? <FlatPreview /> : <Viewport3D />}
          </Suspense>
        </main>
        <Sidebar />
      </div>
    </div>
  );
}
